import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { useCallback } from "react";

import { isNativeMobileApp } from "utils/platform.helpers";

import { ImpactLevel, NotificationLevel, TriggerKind } from "../haptics.utils";

// Global throttle + one-time warn across the whole app
const THROTTLE_MS = 80;
let __hapticsLastRunTs = -THROTTLE_MS;
let __hapticsWarnedOnce = false;

export type HapticsOptions = { disabled?: boolean };

export function useHaptics() {
  const available = isNativeMobileApp();

  const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const safe = async <T>(fn: () => Promise<T> | T) => {
    try {
      if (!available) {
        return;
      }
      await fn();
    } catch (error) {
      if (!__hapticsWarnedOnce) {
        console.error("[haptics] Haptic operation failed:", error);
        __hapticsWarnedOnce = true;
      }
    }
  };

  const run = async <T>(fn: () => Promise<T> | T) =>
    safe(async () => {
      const now = Date.now();

      if (now - __hapticsLastRunTs < THROTTLE_MS) {
        return;
      }
      __hapticsLastRunTs = now;
      await fn();
    });

  // Internal helpers: perform haptic actions without throttling
  const impactRaw = async (level: ImpactLevel, opts?: HapticsOptions) => {
    if (opts?.disabled) {
      return;
    }
    let style: any;
    if (level === "heavy") {
      style = ImpactStyle.Heavy;
    } else if (level === "medium") {
      style = ImpactStyle.Medium;
    } else {
      style = ImpactStyle.Light;
    }
    await Haptics.impact({ style });
  };

  const selectionRaw = async (opts?: HapticsOptions) => {
    if (opts?.disabled) {
      return;
    }
    await Haptics.selectionChanged();
  };

  const notificationRaw = async (type: NotificationLevel, opts?: HapticsOptions) => {
    if (opts?.disabled) {
      return;
    }
    let t: any;
    if (type === "success") {
      t = NotificationType.Success;
    } else if (type === "warning") {
      t = NotificationType.Warning;
    } else {
      t = NotificationType.Error;
    }
    await Haptics.notification({ type: t });
  };

  const impact = useCallback(
    (level: ImpactLevel, opts?: HapticsOptions) => run(async () => impactRaw(level, opts)),
    [available],
  );

  const selection = useCallback(
    (opts?: HapticsOptions) => run(async () => selectionRaw(opts)),
    [available],
  );

  const notification = useCallback(
    (type: NotificationLevel, opts?: HapticsOptions) =>
      run(async () => notificationRaw(type, opts)),
    [available],
  );

  const triggerHaptic = useCallback(
    (kind: TriggerKind, opts?: HapticsOptions) => {
      switch (kind) {
        case "primary":
          return impact("medium", opts);
        case "secondary":
          return impact("light", opts);
        case "checkbox":
          return selection(opts);
        case "select":
          return selection(opts);
        case "reward":
          return run(async () => {
            await impactRaw("light", opts); // quick spark
            await delay(18);
            await selectionRaw(opts); // subtle tick
            await delay(22);
            await notificationRaw("success", opts); // main pop
            await delay(24);
            await impactRaw("medium", opts); // satisfying finish
          });
        case "error":
          return notification("error", opts);
        case "none":
        default:
          return Promise.resolve();
      }
    },
    [impact, selection, notification, available],
  );

  return {
    enabled: available,
    impact,
    selection,
    notification,
    triggerHaptic,
  };
}
